import { useBoolean } from '@chakra-ui/react';
import { CustomOnResize, CustomOnResizeEnd, CustomOnResizeStart } from 'modules/Editor/components/BoundingBox';
import { useAppSelector } from 'modules/Editor/store';
import { selectFormState } from 'modules/Editor/store/interactionSelector';
import { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { isCornerHandle, isLeftSideHandle, isSideHandle } from 'utils/boundingBox';
import { WidgetState } from 'widgets/TextBasedWidgets/common/TextBasedWidgets.types';
import { ReadOnlyRenderType } from 'widgets/Widget.types';
import {
  ReadOnlyWidgetBase,
  WidgetBase,
  WidgetSideMenuSettings,
  WidgetToolbar,
  useEditor,
  useWidget,
} from 'widgets/sdk';

import { FORM_INPUT_TEST_ID } from './FormWidget.config';
import { getMinSize } from './FormWidget.helpers';
import { FormProps, FormType, FormWidgetData } from './FormWidget.types';
import FormWidgetSideMenuSettings from './FormWidgetSideMenuSettings/FormWidgetSideMenuSettings';
import { FormWidgetToolbar } from './FormWidgetToolbar';
import { FORM_TYPE_MAP } from './types';

const SPACE_WITHIN_BOX = 8;

export const FormWidget = () => {
  const [widgetState, setNewWidgetState] = useState(WidgetState.Default);
  const { widgetId, field, style, labelPosition, isWidgetSelected, type, textAreaHeightPx, selectionFieldData } =
    useWidget<FormWidgetData>();
  const { boundingBox, openWidgetSideMenu, widgetRefs } = useEditor();
  const [isLabelFocused, setIsLabelFocused] = useBoolean(false);
  const isActive = widgetState === WidgetState.Active;
  const previousHeightRef = useRef<number | null>(null);
  const labelRef = useRef<HTMLLabelElement>(null);
  // Cache the label height, to avoid dymamically updated labelHeight during resize
  // Use fontSize since labelHeight is fluctuated from layout switching
  const cachedLabelHeight = style.fontSize || 0;
  const isLongQuestion = type === FormType.LongQuestion;

  const startLeftRightPx = useRef<[number, number]>([0, 0]);

  const FormBase = FORM_TYPE_MAP[type] as React.FC<FormProps>;

  const setWidgetState = useCallback(
    (newWidgetState: WidgetState) => {
      setNewWidgetState(newWidgetState);
      if (newWidgetState === WidgetState.Active || newWidgetState === WidgetState.Default) {
        setIsLabelFocused.off();
      } else if (newWidgetState === WidgetState.Edit) {
        setIsLabelFocused.on();
      }
    },
    [setNewWidgetState, setIsLabelFocused],
  );

  const onClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      if (widgetState === WidgetState.Active && e.target instanceof HTMLLabelElement) {
        setWidgetState(WidgetState.Edit);
      }
      // If click target does not match label element while being still selected, keep it in active state
      if (
        isWidgetSelected &&
        widgetState === WidgetState.Edit &&
        (e.target as HTMLElement)?.dataset.testid === FORM_INPUT_TEST_ID
      ) {
        setWidgetState(WidgetState.Active);
      }
      if (widgetState === WidgetState.Default) setWidgetState(WidgetState.Active);
    },
    [isWidgetSelected, setWidgetState, widgetState],
  );

  // If the widget is not selected anymore, reset the widget state to default
  useEffect(() => {
    if (!isWidgetSelected && widgetState !== WidgetState.Default) {
      setWidgetState(WidgetState.Default);
    }
    // a widget can be selected without going through onClick (e.g. keyboard tab, drag area)
    // a selected text widget should never be in default state
    if (isWidgetSelected && widgetState === WidgetState.Default) {
      setWidgetState(WidgetState.Active);
    }
  }, [isWidgetSelected, widgetState, setWidgetState]);

  // Update bounding box when field, style or label position changes
  useEffect(() => {
    // Use setTimeout as nextTick to update bounding box after rendering the component
    setTimeout(() => boundingBox.updateRect(), 0);
  }, [boundingBox, field, style, labelPosition, style.lineHeight, textAreaHeightPx]);

  /**
   * Update bounding box for the selection field only
   * - If the inner content is wider than the widget container, set the width to the scrollWidth
   * - Set the height to fit-content by default
   */
  useEffect(() => {
    if (type !== FormType.SelectionField) return;

    const currentTarget = widgetRefs[widgetId]?.element;
    if (!currentTarget) return;

    if (currentTarget.scrollWidth > currentTarget.clientWidth) {
      currentTarget.style.width = `${currentTarget.scrollWidth}px`;
    }

    if (currentTarget.style.height !== 'fit-content') {
      currentTarget.style.height = 'fit-content';
    }

    setTimeout(() => {
      boundingBox.updateRect();
    }, 0);
  }, [widgetId, widgetRefs, type, boundingBox, selectionFieldData, style.borderWidth]);

  const customOnResizeStart = useCallback(({ event, onResizeStart }: CustomOnResizeStart) => {
    onResizeStart(event);

    const target = event.target;
    if (target instanceof HTMLElement) {
      startLeftRightPx.current = [target.offsetLeft, target.offsetLeft + target.offsetWidth];
    }
  }, []);

  const customOnResize = useCallback(
    ({ event, onResize, frameMap }: CustomOnResize) => {
      onResize(event);

      const isSide = isSideHandle(event.direction);

      // Check the min width using the scrollWidth of the target element
      if (type === FormType.SelectionField) {
        const target = event.target as HTMLElement;
        const frame = frameMap.get(event.target);
        const [distX] = event.dist;
        const isLeftHandle = isSide && isLeftSideHandle(event.direction);

        // After changing the width above, check whether the maximum width has been exceeded.
        const enableMinWidth = target.clientWidth < target.scrollWidth;

        if (enableMinWidth) {
          target.style.width = `${target.scrollWidth}px`;
        }

        // Update Translate
        if (isLeftHandle && enableMinWidth) {
          const [startLeftPx, startRightPx] = startLeftRightPx.current;
          const currentRightPx = target.offsetLeft + startLeftPx + distX + target.scrollWidth;
          const diff = currentRightPx - startRightPx;
          const newTranslateX = startLeftPx + distX - diff;

          target.style.transform = `translate(${newTranslateX}px, ${0}px) rotate(0deg)`;
          frame.translate = [newTranslateX, 0];
        }
        return;
      }

      let newHeight = event.target.clientHeight ?? event.height;
      let newWidth = event.target.clientWidth ?? event.width;

      const { minWidth, minHeight } = getMinSize(cachedLabelHeight, style.fontSize, labelPosition);
      if (newWidth < minWidth) {
        newWidth = minWidth;
      }
      if (isLongQuestion) {
        const textareaElement = document.getElementById(widgetId)?.querySelector('textarea');
        if (!isSide && textareaElement) {
          const currentLabelHeight = labelRef.current?.clientHeight || 0;
          const shouldUpdateHeight = labelPosition !== 'left' && field.label;
          const heightOfRest = currentLabelHeight + style.gap + SPACE_WITHIN_BOX;
          const updatedHeight = shouldUpdateHeight ? newHeight - heightOfRest : newHeight - SPACE_WITHIN_BOX;
          (textareaElement as HTMLTextAreaElement).style.height = `${updatedHeight}px`;
        }
        if (!isSide && previousHeightRef.current && previousHeightRef.current > newHeight && newHeight < minHeight) {
          newHeight = minHeight;
        }

        previousHeightRef.current = newHeight;
      }

      event.target.style.width = `${newWidth}px`;
      event.target.style.height = isSide ? 'fit-content' : `${newHeight}px`;
    },
    [type, cachedLabelHeight, style.fontSize, style.gap, labelPosition, isLongQuestion, widgetId, field.label],
  );

  const customOnResizeEnd = ({ onResizeEnd, event }: CustomOnResizeEnd) => {
    const isResizingFromCorner = isCornerHandle(event.lastEvent.direction);
    if (isLongQuestion && isResizingFromCorner) {
      const textareaElement = document.getElementById(widgetId)?.querySelector('textarea');
      if (textareaElement) {
        onResizeEnd(event, { textAreaHeightPx: parseFloat(textareaElement.style.height) });
      }
    } else {
      onResizeEnd(event);
    }

    event.target.style.height = 'fit-content';
  };

  return (
    <>
      <WidgetToolbar>
        <FormWidgetToolbar isActive={isActive} />
      </WidgetToolbar>
      <WidgetBase
        onResizeStart={customOnResizeStart}
        onResize={customOnResize}
        onResizeEnd={customOnResizeEnd}
        onClick={onClick}
        onDoubleClick={openWidgetSideMenu}
      >
        <WidgetSideMenuSettings>
          <FormWidgetSideMenuSettings isLabelFocused={isLabelFocused} />
        </WidgetSideMenuSettings>
        <FormBase
          ref={labelRef as React.RefObject<HTMLLabelElement> | undefined}
          isLabelFocused={isLabelFocused}
          setWidgetState={setWidgetState}
        />
      </WidgetBase>
    </>
  );
};

export const ReadOnlyFormWidget = () => {
  const { type, widgetId, renderType } = useWidget<FormWidgetData>();
  const FormBase = FORM_TYPE_MAP[type];
  const formState = useAppSelector(selectFormState(widgetId));
  const isThumbnailReadOnly = renderType === ReadOnlyRenderType.ThumbnailReadOnly;

  return (
    <ReadOnlyWidgetBase>
      <FormBase isReadOnly disableTabbability={isThumbnailReadOnly} storedValue={formState?.fields?.value || ''} />
    </ReadOnlyWidgetBase>
  );
};
